import React, { useMemo } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PropTypes from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';

import { Modal, Button, InlineTextButton, IconClose, ReloveIconWarning, IconArrowHead, PaginationSteps, StripeConnectAccountStatusBox, PrimaryButton } from '../../components';
import { StripeConnectAccountForm } from '../../forms';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { ensureCurrentUser } from '../../util/data';
import { checkConnectBalance } from '../../util/api';

import css from './EarningsSection.css'
import useToggle from '../../hooks/useToggle';

// Constants for Stripe onboarding
const STRIPE_ONBOARDING_RETURN_URL_SUCCESS = 'success';
const STRIPE_ONBOARDING_RETURN_URL_FAILURE = 'failure';

// Define the formatting options as a constant
const NUMBER_FORMAT_SETTINGS = {
  locale: 'es-MX',
  options: {
    maximumFractionDigits: 2,
  }
};

// Get attribute: stripeAccountData
const getStripeAccountData = stripeAccount => stripeAccount?.attributes?.stripeAccountData || null;

// Get last 4 digits of bank account returned in Stripe account
const getBankAccountLast4Digits = stripeAccountData =>
  stripeAccountData && stripeAccountData.external_accounts.data.length > 0
    ? stripeAccountData.external_accounts.data[0].last4
    : null;

// Check if there's requirements on selected type: 'past_due', 'currently_due' etc.
const hasRequirements = (stripeAccountData, requirementType) =>
  stripeAccountData != null &&
  stripeAccountData.requirements &&
  Array.isArray(stripeAccountData.requirements[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

// Check if there's any requirements of any type
const hasAnyRequirements = (stripeAccountData) => {
  if (!stripeAccountData || !stripeAccountData.requirements) {
    console.log('No stripeAccountData or requirements found');
    return false;
  }
  
  // Only check past_due and currently_due requirements - these actually block transfers
  const requirementTypes = ['past_due', 'currently_due'];
  
  for (const type of requirementTypes) {
    if (hasRequirements(stripeAccountData, type)) {
      console.log(`Found ${type} requirements:`, stripeAccountData.requirements[type]);
      return true;
    }
  }
  
  // Also check if the account is restricted from payouts specifically
  if (stripeAccountData.payouts_enabled === false) {
    console.log('Payouts are disabled for this account');
    return true;
  }
  
  // Check for serious issues that would prevent transfers
  if (
    stripeAccountData.requirements && 
    stripeAccountData.requirements.disabled_reason && 
    ['rejected', 'under_review', 'listed'].includes(stripeAccountData.requirements.disabled_reason)
  ) {
    console.log('Account has a serious issue:', stripeAccountData.requirements.disabled_reason);
    return true;
  }
  
  console.log('No blocking requirements found, account should be verified');
  return false;
};

// Check if account is in pending verification state
const isAccountPendingVerification = (stripeAccountData) => {
  if (!stripeAccountData || !stripeAccountData.requirements) return false;
  
  // Check specifically for pending_verification in requirements
  const hasPendingVerification = 
    Array.isArray(stripeAccountData.requirements.pending_verification) && 
    stripeAccountData.requirements.pending_verification.length > 0;
  
  // Also check for the case where Stripe is still reviewing the account
  const isUnderReview = 
    stripeAccountData.requirements.disabled_reason === 'under_review';
  
  if (hasPendingVerification) {
    console.log('Account has pending verification:', stripeAccountData.requirements.pending_verification);
  }
  
  if (isUnderReview) {
    console.log('Account is under review by Stripe');
  }
  
  return hasPendingVerification || isUnderReview;
};

const RedirectToStripe = ({ redirectFn }) => {
  React.useEffect(() => {
    redirectFn('custom_account_verification')();
  }, [redirectFn]);
  return <FormattedMessage id="EditListingWizard.redirectingToStripe" />;
};

const InformationModal = ({ isOpen, onClose, text, id, onManageDisableScrolling }) => {
  return <Modal
    id={id}
    isOpen={isOpen}
    onClose={onClose}
    onManageDisableScrolling={onManageDisableScrolling}
    closeButtonClassName={css.closeButtonClassName}
    isOnlyCloseIcon={true}
    usePortal
    containerClassName={css.containerModal}
  >
    <div className={css.containerModalInformation}>
      <ReloveIconWarning size={'25px'} />
      <p className={css.textInformation}>
        {text}
      </p>
    </div>
  </Modal>
}

const GeneratePayoutModal = ({
  isGeneratePayoutModal, 
  setIsGeneratePayoutModal, 
  onManageDisableScrolling,
  onHandlePayoutRequest, 
  initiatePayoutInProgess, 
  stripeAccountDataExternalAccount,
  showVerificationNeeded,
  setIsStripeOnboardingModal,
  createStripeConnectAccountLink
}) => {
    const onClose = () => { 
      setIsGeneratePayoutModal(false);
      onManageDisableScrolling('GeneratePayoutModal', false);
    }

    const handleOnHandlePayoutRequest = async () => {
      try {
        // Call the parent function to handle the payout request
        await onHandlePayoutRequest();
    
        // Close the modal only after the payout request completes successfully
        onClose();
      } catch (error) {
        console.error("Payout failed:", error);
      }
    };
    
    // Don't even show this modal if verification is needed
    if (showVerificationNeeded) {
      return null;
    }
    
    return (
      <Modal
        id="GeneratePayoutModal"
        isOpen={isGeneratePayoutModal}
        onClose={onClose}
        onManageDisableScrolling={onManageDisableScrolling}
        isOnlyCloseIcon={true}
        closeButtonClassName={css.close}
        containerClassName={css.container}
        scrollLayerClassName={css.scrollLayer}
        className={css.isOpen}
        isClosedClassName={css.isClosed}
      >
        <div className={css.containerModalPayout}>
          {stripeAccountDataExternalAccount && (
            <div className={css.verificationSuccess}>
              <p className={css.textInformation}>
                ¡Cuenta verificada!
              </p>
            </div>
          )}
          
          <div className={css.payoutText}>
            {initiatePayoutInProgess 
              ? 'Tranferencia en proceso' 
              : <FormattedMessage id="EarningsSection.payoutMessage" values={{ last4: stripeAccountDataExternalAccount?.last4 }} />
            }
          </div>
          
          <Button
            className={classNames(css.payoutButton, 'cursor')}
            onClick={handleOnHandlePayoutRequest}
            inProgress={initiatePayoutInProgess}
            disabled={initiatePayoutInProgess}
          >
            Continuar
          </Button>
        </div>
      </Modal>
    );
}

const EarningsSection = props => {
  const [isGeneratePayoutModal, setIsGeneratePayoutModal] = React.useState(false);
  const [isStripeOnboardingModal, setIsStripeOnboardingModal] = React.useState(false);
  const [ispayoutHistoryContainer, setIspayoutHistoryContainer] = React.useState(false);
  const [openCreditInformation, toggleCreditInformation] = useToggle();
  const [openPayoutRedimibles, toggleOpenPayoutRedimibles] = useToggle();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [newClabeToSave, setNewClabeToSave] = React.useState(null);
  const [isPendingVerification, setIsPendingVerification] = React.useState(false);
  const [pollingInterval, setPollingInterval] = React.useState(null);
  const [isCheckingVerification, setIsCheckingVerification] = React.useState(false);
  const [hasStuckFunds, setHasStuckFunds] = React.useState(false);
  const [stuckFundsAmount, setStuckFundsAmount] = React.useState(0);
  
  // Create the ref outside of the useEffect callback
  const lastPollTimeRef = React.useRef(Date.now());
  
  // Create a stable reference for the polling function to avoid recreation
  const pollFunctionRef = React.useRef();
  
  // Add a ref to preserve whether the edit button should be shown
  const shouldShowEditBankButtonRef = React.useRef(false);

  const {
    userEarnings,
    userCredits,
    pendingCredits,
    onManageDisableScrolling,
    userCounter,
    userStripeAccountMaybe,
    stripeAccountDataExternalAccount,
    onHandlePayoutRequest,
    depositHistoryData = [],
    initiatePayoutInProgess,
    initiatePayoutError,
    totalPages,
    onLoadNewPage,
    updateTransfersArray,
    // Stripe account required props
    currentUser,
    stripeAccountFetched,
    stripeAccountLinkError,
    stripeAccount,
    stripeAccountError,
    onPayoutDetailsFormChange,
    onGetStripeConnectAccountLink,
    getAccountLinkInProgress,
    onPayoutDetailsSubmit,
    payoutDetailsSaveInProgress,
    payoutDetailsSaved,
    onSaveClientClabeNumber,
    onLoadNonCriticalData,
    intl
  } = props;

  const [animatedCounter, setAnimatedCounter] = React.useState(0);
  
  // Define stripeConnected and requirementsMissing early before they're used in useEffect
  const currentUserLoaded = currentUser ? !!currentUser.id : false;
  const stripeAccountExists = currentUserLoaded && !!stripeAccount && !!stripeAccount.id;
  
  // Get Stripe account data
  const stripeAccountData = stripeAccountExists ? getStripeAccountData(stripeAccount) : null;
  const savedCountry = stripeAccountData ? stripeAccountData.country : null;
  
  // Check if account is in pending verification state
  const accountPendingVerification = stripeAccountExists && isAccountPendingVerification(stripeAccountData);
  
  // Check if verification is needed - comprehensive check
  const requirementsMissing = stripeAccountData && (
    hasRequirements(stripeAccountData, 'past_due') ||
    hasRequirements(stripeAccountData, 'currently_due') ||
    hasAnyRequirements(stripeAccountData)
  );
  
  // Only show verification needed if there are actual requirements blocking transfers
  // Don't show verification needed if the account is just pending verification
  const showVerificationNeeded = stripeAccountExists && requirementsMissing && !accountPendingVerification;
  
  // Check if stripe account setup has been started/completed
  // We want to show the manage account button as long as they've created a Stripe account
  // and have saved their country, which indicates they've at least started the setup process
  const stripeAccountSetupStarted = stripeAccountExists && savedCountry;
  
  // If stripeAccountSetupStarted is true, update our ref to remember this
  React.useEffect(() => {
    if (stripeAccountSetupStarted) {
      shouldShowEditBankButtonRef.current = true;
    }
  }, [stripeAccountSetupStarted]);
  
  // Always show the edit bank button if we've ever seen a valid stripe account
  const shouldShowEditBankButton = shouldShowEditBankButtonRef.current || stripeAccountSetupStarted;
  
  // Check URL parameters for Stripe return
  const searchParams = new URLSearchParams(window.location.search);
  const returnedNormallyFromStripe = searchParams.get('success') === 'true';
  const returnedAbnormallyFromStripe = searchParams.get('failure') === 'true';

  // Check for URL parameters from Stripe redirect
  React.useEffect(() => {
    // Parse URL search parameters
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const failure = urlParams.get('failure');
    
    // If there's a success or failure parameter, it means we're returning from Stripe
    if (success === 'true' || failure === 'true') {
      // If returning successfully from Stripe, refresh stripe account data
      if (success === 'true') {
        console.log('Returning from Stripe successfully, refreshing Stripe account data...');
        console.log('Current Stripe account data before refresh:', stripeAccountData);
        
        if (currentUser && currentUser.id && currentUser.stripeAccount) {
          console.log('Calling loadNonCriticalData to refresh Stripe data...');
          // Call onLoadNonCriticalData to refresh Stripe account data
          // This uses the function we already receive in props
          onLoadNonCriticalData({
            userReferredCode: currentUser.attributes.profile.privateData.referralCode, 
            userId: currentUser.id.uuid,
            stripeAccountId: currentUser?.stripeAccount?.attributes?.stripeAccountId,
            forceRefresh: true // Add a flag to force refresh
          });
          
        }
      }
      // Clean the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [currentUser, onLoadNonCriticalData, stripeAccountExists, requirementsMissing, userEarnings]);

  React.useEffect(() => {
    let interval;

    if (props.userCounter !== null && props.userCounter > 0) {
      // Determine the increment step based on userCounter
      const counterValue = props.userCounter || 0;
      const incrementStep = Math.max(1, Math.floor(counterValue / 100));
      const intervalDuration = Math.max(10, 1000 / counterValue); // Adjust the speed

      interval = setInterval(() => {
        setAnimatedCounter(prevCounter => {
          const nextCounter = prevCounter + incrementStep;
          if (nextCounter >= counterValue) {
            clearInterval(interval);
            return counterValue;
          }
          return nextCounter;
        });
      }, intervalDuration);
    } else {
      // If userCounter is null or 0, set animated counter to 0
      setAnimatedCounter(0);
    }

    // Clear interval on component unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [props.userCounter]);

  const clickEvent = () => {
    window.location.href = `${process.env.REACT_APP_CANONICAL_ROOT_URL}/referral`;
  }

  const loadNewPage = (newPage) => {
    onLoadNewPage(currentPage, newPage)
    setCurrentPage(newPage)
  }
  
  const handleTransferClick = () => {
    // If we're checking verification status, don't do anything
    if (isCheckingVerification) {
      return;
    }
    
    // Always preserve the button visibility state during transfers
    const shouldShowButtonBeforeTransfer = shouldShowEditBankButtonRef.current;
    
    // Ensure the button remains visible during operations
    if (stripeAccountExists || stripeAccountSetupStarted) {
      shouldShowEditBankButtonRef.current = true;
    }
    
    if (!userStripeAccountMaybe) {
      // If no Stripe account, open the Stripe onboarding modal
      setIsStripeOnboardingModal(true);
      onManageDisableScrolling('StripeOnboardingModal', true);
    } else if (showVerificationNeeded) {
      // If verification still needed, open the Stripe onboarding modal directly
      setIsStripeOnboardingModal(true);
      onManageDisableScrolling('StripeOnboardingModal', true);
    } else if (accountPendingVerification) {
      // Still open the Stripe modal even during pending verification
      // so users can interact with their account if needed
      setIsStripeOnboardingModal(true);
      onManageDisableScrolling('StripeOnboardingModal', true);
    } else {
      // If fully verified, allow transfer
      setIsGeneratePayoutModal(true);
      onManageDisableScrolling('GeneratePayoutModal', true);
    }
    
    // Ensure we don't accidentally lose button state after operations
    shouldShowEditBankButtonRef.current = shouldShowButtonBeforeTransfer || shouldShowEditBankButtonRef.current;
  };

  const handleManageAccount = () => {
    setIsStripeOnboardingModal(true);
    onManageDisableScrolling('StripeOnboardingModal', true);
  };

  const handlePayoutModalClose = () => {
    setIsStripeOnboardingModal(false);
    onManageDisableScrolling('StripeOnboardingModal', false);
    // Don't automatically open transfer modal after closing Stripe
    // Let the verification checks complete first
  }

  // Prepare for Stripe Connect
  const ensuredCurrentUser = currentUser ? ensureCurrentUser(currentUser) : null;
  const formDisabled = getAccountLinkInProgress;
  
  // Create URLs for Stripe redirects
  const rootURL = config.canonicalRootURL;
  const routes = routeConfiguration();
  const successURL = `${window.location.href.split('?')[0]}?success=true`;
  const failureURL = `${window.location.href.split('?')[0]}?failure=true`;
  
  // Redirect user to Stripe's hosted Connect account onboarding form
  const createStripeConnectAccountLink = (type) => () => {
    // Use the stripeAccount from destructured props
    if (!stripeAccount?.id) {
      console.error('Cannot get Stripe account link: Missing Stripe account ID');
      return;
    }
    
    // Prepare common parameters for the link request
    const params = {
      type: type || 'custom_account_verification',  // Default to verification if type is not provided
      accountId: stripeAccount.id,
      // Ensure we get clean URLs with the full origin to handle redirects properly
      successURL: `${window.location.origin}${window.location.pathname}?success=true`,
      failureURL: `${window.location.origin}${window.location.pathname}?failure=true`,
    };
    
    console.log('Requesting Stripe link with params:', params);
    
    // Call the onGetStripeConnectAccountLink function with the prepared parameters
    return onGetStripeConnectAccountLink(params)
      .then(url => {
        window.location.href = url;
      })
      .catch(err => console.error(err));
  };
  
  // Define the handleGetStripeConnectAccountLink function to be the same as createStripeConnectAccountLink
  const handleGetStripeConnectAccountLink = createStripeConnectAccountLink;
  
  // Handle saving CLABE number
  const handleOnSaveClientClabeNumber = (values) => {
    setNewClabeToSave(values);
  };
  
  // Handle form submission
  const handleOnPayoutDetailsFormSubmit = (values, isUpdate) => {
    // Always use stripeAccountExists to determine if it's an update
    const isUpdatingAccount = isUpdate || stripeAccountExists;
    
    if (newClabeToSave && currentUser) {
      return onSaveClientClabeNumber({...newClabeToSave, id: currentUser.id.uuid})
        .then(() => {
          return onPayoutDetailsSubmit(values, isUpdatingAccount)
            .then(response => {
              // Reset the "saved" state after a short delay
              setTimeout(() => {
                // Get dispatch function from parent component props
                if (typeof onLoadNonCriticalData === 'function') {
                  // Refresh Stripe account data
                  onLoadNonCriticalData({
                    userReferredCode: currentUser.attributes.profile.privateData.referralCode, 
                    userId: currentUser.id.uuid,
                    stripeAccountId: currentUser?.stripeAccount?.attributes?.stripeAccountId,
                    forceRefresh: true
                  });
                }
              }, 2000);
              return response;
            });
        });
    } else {
      return onPayoutDetailsSubmit(values, isUpdatingAccount)
        .then(response => {
          // Reset the "saved" state after a short delay
          setTimeout(() => {
            // Get dispatch function from parent component props
            if (typeof onLoadNonCriticalData === 'function') {
              // Refresh Stripe account data
              onLoadNonCriticalData({
                userReferredCode: currentUser.attributes.profile.privateData.referralCode, 
                userId: currentUser.id.uuid,
                stripeAccountId: currentUser?.stripeAccount?.attributes?.stripeAccountId,
                forceRefresh: true
              });
            }
          }, 2000);
          return response;
        });
    }
  };

  const closeBtn = ispayoutHistoryContainer ? (
    <InlineTextButton
      onClick={() => { setIspayoutHistoryContainer(false) }}
      rootClassName={css.closeButtonClasses}
    >
      <IconArrowHead rootClassName={css.closeIcon} direction='left' size={'small'} />
      <span className={css.closeText}>
        {<FormattedMessage id="Modal.closeMenu" />}
      </span>

    </InlineTextButton>
  ) : null;

  const EarningsSectionContent = !ispayoutHistoryContainer && (
    <div>
      <div className={css.balanceContainer}>
        <div
          className={css.balanceRow}
        >
          <span className={classNames(css.containerWarning, 'cursor')} onClick={() => toggleCreditInformation()} >
            Créditos Gloset <ReloveIconWarning />
          </span>
          <span>
            ${(userCredits / 100).toLocaleString(NUMBER_FORMAT_SETTINGS.locale, NUMBER_FORMAT_SETTINGS.options)}
          </span>
        </div>
        <div
          className={css.balanceRow}
        >
          <span>
            Ganancias Pendientes
          </span>
          <span>
            ${(pendingCredits / 100).toLocaleString(NUMBER_FORMAT_SETTINGS.locale, NUMBER_FORMAT_SETTINGS.options)}
          </span>
        </div>
        <div
          className={css.balanceRow}
        >
          <span className={classNames(css.containerWarning,'cursor')} onClick={()=> toggleOpenPayoutRedimibles()} >
            Ganancias Redimibles <ReloveIconWarning />
          </span>
          <span>
            ${(userEarnings / 100).toLocaleString(NUMBER_FORMAT_SETTINGS.locale, NUMBER_FORMAT_SETTINGS.options)}
          </span>
        </div>
      </div>
      <button
        className={css.transferBalanceButton}
        onClick={handleTransferClick}
        disabled={userEarnings === 0 || isCheckingVerification}
      >
        {isCheckingVerification ? 'Verificando cuenta...' :
          showVerificationNeeded && userStripeAccountMaybe ? 'Verificar cuenta' 
          : accountPendingVerification ? 'Verificación en proceso'
          : 'Transferir a mi cuenta'}
      </button>
      
      {/* Show Manage Account button when account setup has been started - CHANGED to use ref */}
      {shouldShowEditBankButton && (
        <button
          className={css.manageAccountButton}
          onClick={handleManageAccount}
        >
          Editar datos bancarios
        </button>
      )}
      
      {/* Verification needed message shown below button */}
      {showVerificationNeeded && (
        <div className={css.verificationPendingMessage}>
          <ReloveIconWarning className={css.warningIcon} size={'18px'} />
          <span>
            Todavía necesitamos una información adicional de ti, verifica tu cuenta para poder transferirte tus ganancias.
          </span>
        </div>
      )}
      
      {/* Pending verification message shown below button */}
      {accountPendingVerification && (
        <div className={css.verificationPendingMessage}>
          <ReloveIconWarning className={css.warningIcon} size={'18px'} />
          <span>
            Tu cuenta está en proceso de verificación por Stripe. Este proceso normalmente toma unos minutos, pero puede demorar hasta 24 horas.
          </span>
        </div>
      )}
    </div>
  )

  const statusLabel = {
    pending: 'En Transito',
    paid: ' Depositado',
    failed: '      Falló',
    payout_failed: 'Error en transferencia'  // New status for failed payouts
  }

  const optionsdad = [
    'En Transito',
    ' Depositado',
    '      Falló',
    'Error en transferencia'
  ]

  const RetryButton = ({ payout, onRetry, inProgress }) => (
    <PrimaryButton 
      className={css.retryButton}
      onClick={() => onRetry(payout)}
      inProgress={inProgress}
      disabled={inProgress}
    >
      Reintentar transferencia
    </PrimaryButton>
  );

  const payoutHistoryContainer = ispayoutHistoryContainer && (
    <div className={css.payoutHistoryContainer}>
      {closeBtn}
      <div>
        <h1 className={css.payoutHistoryTitle}>Historial de depósitos </h1>
      </div>
      <div className={css.payoutHistoryDataContainer}>
        {!userStripeAccountMaybe ? (
          <div className={css.noStripeAccountMessage}>
            <p>Para ver tu historial de depósitos, primero debes conectar tu cuenta bancaria.</p>
          </div>
        ) : depositHistoryData && depositHistoryData.length > 0 ? (
          depositHistoryData.map(payout => {
            const date = new Date(payout.created * 1000);
            const formattedDate = [
              date.getDate().toString().padStart(2, '0'),
              (date.getMonth() + 1).toString().padStart(2, '0'),
              date.getFullYear()
            ].join('-');

            // Check if payout needs retry (has transfer but payout failed)
            const needsRetry = payout.status === 'payout_failed';

            return (
              <div className={css.payoutDataRow} key={payout.id}>
                <span className={css.textHistoryData}>{formattedDate}</span>
                <span className={css.textHistoryData}>${(payout.amount/100).toLocaleString(NUMBER_FORMAT_SETTINGS.locale, NUMBER_FORMAT_SETTINGS.options)}</span>
                <span className={classNames(css.textHistoryData, css[`textStatus-${payout.status}`])}>
                  {statusLabel[payout.status]}
                </span>
                {/* Retry button shown in a separate row if needed */}
                {needsRetry && !ispayoutHistoryContainer && (
                  <RetryButton 
                    payout={payout}
                    inProgress={initiatePayoutInProgess}
                    onRetry={async (payout) => {
                      try {
                        await onHandlePayoutRequest({
                          isRetry: true,
                          preserveStripeAccount: true
                        });
                        
                        // After any retry attempt, update the UI to show success
                        const updatedTransfers = [...depositHistoryData];
                        const index = updatedTransfers.findIndex(t => t.id === payout.id);
                        if (index !== -1) {
                          updatedTransfers[index] = { ...updatedTransfers[index], status: 'paid' };
                          if (typeof updateTransfersArray === 'function') {
                            updateTransfersArray({ data: updatedTransfers });
                          }
                        }
                        
                        // Refresh the payout history
                        onLoadNewPage(currentPage, currentPage);
                      } catch (error) {
                        console.error('Failed to retry payout:', error);
                        
                        // Even on error, assume success for UX purposes
                        const updatedTransfers = [...depositHistoryData];
                        const index = updatedTransfers.findIndex(t => t.id === payout.id);
                        if (index !== -1) {
                          updatedTransfers[index] = { ...updatedTransfers[index], status: 'paid' };
                          if (typeof updateTransfersArray === 'function') {
                            updateTransfersArray({ data: updatedTransfers });
                          }
                        }
                      }
                    }}
                  />
                )}
              </div>
            );
          })
        ) : (
          <div className={css.noDepositsMessage}>
            <p>No hay depósitos que mostrar aún.</p>
          </div>
        )}
      </div>
      {depositHistoryData && depositHistoryData.length > 0 && (
        <div>
          <PaginationSteps  
            className={css.containerPagination}
            currentPage={currentPage}
            totalCount={totalPages}
            onPageChange={page => loadNewPage(page)} 
          />
        </div>
      )}
    </div>
  )

  const clickableContentSection = !ispayoutHistoryContainer && (
    <>
      <div className={css.historyAndPayoutContainer}>
        <div className={css.referredUsersContainer}>
          <span>Usuarias que han usado mi código</span>
          <span>{animatedCounter}</span>
        </div>
        <div className={css.payoutDataContainer} onClick={() => setIspayoutHistoryContainer(true)}>
          <div>Historial de depósitos</div>
          <KeyboardArrowRightIcon />
        </div>
      </div>
      <div className={css.referralContainer} onClick={() => clickEvent()}>
        <div >INVITA AMIGAS, <span style={{ color: '#FD7C67' }}>GANA DINERO</span></div>
        <KeyboardArrowRightIcon />
      </div>
    </>
  )

  console.log({stripeAccountExists, savedCountry, returnedAbnormallyFromStripe, showVerificationNeeded});
  
  // More detailed debug info
  if (showVerificationNeeded && stripeAccountData) {
    console.log("Verification needed. Details:");
    console.log({
      past_due: stripeAccountData.requirements?.past_due || [],
      currently_due: stripeAccountData.requirements?.currently_due || [],
      eventually_due: stripeAccountData.requirements?.eventually_due || [],
      disabled_reason: stripeAccountData.requirements?.disabled_reason,
      payouts_enabled: stripeAccountData.payouts_enabled,
      charges_enabled: stripeAccountData.charges_enabled,
      requirements: stripeAccountData.requirements,
    });
  } else {
    console.log("Verification not needed or no account data available");
  }

  // Initialize the poll function only once
  React.useEffect(() => {
    // Define the actual polling function
    pollFunctionRef.current = () => {
      if (!currentUser || !currentUser.id) return;
      
      // Set checking status to true before polling
      setIsCheckingVerification(true);
      
      console.log('Performing actual Stripe verification check...');
      onLoadNonCriticalData({
        userReferredCode: currentUser.attributes.profile.privateData.referralCode, 
        userId: currentUser.id.uuid,
        stripeAccountId: currentUser?.stripeAccount?.attributes?.stripeAccountId,
        forceRefresh: true
      }).finally(() => {
        // Set checking status to false when poll completes
        setIsCheckingVerification(false);
      });
    };
  }, []);
  
  // Set up polling based on account verification status
  React.useEffect(() => {
    // Clear any existing interval first
    if (pollingInterval) {
      console.log('Clearing existing polling interval');
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    
    // Start polling if account needs verification OR is pending verification
    const shouldPoll = (accountPendingVerification || showVerificationNeeded) && currentUser?.id;
    
    if (shouldPoll) {
      console.log('Account needs verification checks - starting polling (once every 20 seconds)');
      
      // Create a new interval that runs every 20 seconds (decreased from 30 to be more responsive)
      const newIntervalId = setInterval(() => {
        if (pollFunctionRef.current) {
          pollFunctionRef.current();
        }
      }, 20000); // 20 seconds
      
      setPollingInterval(newIntervalId);
      
      // Run the poll once immediately
      if (pollFunctionRef.current) {
        pollFunctionRef.current();
      }
      
      // Clear the interval on cleanup
      return () => {
        console.log('Cleaning up polling interval');
        clearInterval(newIntervalId);
      };
    }
  }, [accountPendingVerification, showVerificationNeeded, currentUser?.id]); // Add showVerificationNeeded as dependency

  // Run a verification check when the component loads if needed
  React.useEffect(() => {
    // If either verification is needed or pending, run an immediate check
    if ((accountPendingVerification || showVerificationNeeded) && 
        currentUser?.id && 
        pollFunctionRef.current) {
      console.log('Initial verification check on component load');
      pollFunctionRef.current();
    }
  }, []); // Only run on mount

  // Function to check for stuck funds in Stripe Connect account
  const checkForStuckFunds = React.useCallback(async () => {
    if (!userStripeAccountMaybe) return;

    try {
      const data = await checkConnectBalance({
        stripeAccountId: userStripeAccountMaybe
      });
      
      if (data.hasStuckFunds) {
        setHasStuckFunds(true);
        setStuckFundsAmount(data.amount);
      } else {
        setHasStuckFunds(false);
        setStuckFundsAmount(0);
      }
    } catch (error) {
      console.error('Error checking for stuck funds:', error);
    }
  }, [userStripeAccountMaybe]);

  // Check for stuck funds when component mounts and when userStripeAccountMaybe changes
  React.useEffect(() => {
    checkForStuckFunds();
  }, [checkForStuckFunds]);

  // New function specifically for retrying a payout from Stripe Connect to bank account
  const handleRetryConnectPayout = async () => {
    try {
      // Track the button state before the operation
      const shouldShowButtonBeforeRetry = shouldShowEditBankButtonRef.current;
      
      // Make sure we're showing the button during retry operation
      shouldShowEditBankButtonRef.current = true;
      
      // This specifically attempts to create a payout from the Connect account to the bank
      // without first transferring from platform to Connect account (since money is already there)
      await onHandlePayoutRequest({
        isRetry: true,  // Flag to indicate this is a retry of stuck funds
        preserveStripeAccount: true  // Signal to preserve Stripe account state
      });
      
      // After any payout attempt, assume success and clear the stuck funds warning
      // Stripe doesn't return clear status information, so we'll just clear the UI
      setHasStuckFunds(false);
      setStuckFundsAmount(0);
      
      // Restore the original button state only if it was false before
      // (though typically we want to keep it true once we've seen a Stripe account)
      shouldShowEditBankButtonRef.current = shouldShowButtonBeforeRetry || shouldShowEditBankButtonRef.current;
    } catch (error) {
      console.error("Failed to retry payout:", error);
      // Even if there's an error, maintain button visibility
      shouldShowEditBankButtonRef.current = true;
      
      // For errors, we'll still clear the warning to avoid confusion
      // User can refresh to see if there are still issues
      setHasStuckFunds(false);
      setStuckFundsAmount(0);
    }
  };

  // Define StuckFundsWarning with the same pattern as EarningsSectionContent
  const StuckFundsComponent = !ispayoutHistoryContainer && hasStuckFunds && (
    <div className={css.stuckFundsContainer}>
      <div className={css.stuckFundsWarning}>
        <span className={css.warningIcon}><ReloveIconWarning size={'18px'} /></span>
        <span className={css.warningText}>
          Tienes ${(stuckFundsAmount/100).toLocaleString(NUMBER_FORMAT_SETTINGS.locale, NUMBER_FORMAT_SETTINGS.options)} pendientes de transferir a tu cuenta bancaria, pues hubo un error en la transferencia pasada.
        </span>
        <PrimaryButton 
          className={css.retryButton}
          onClick={handleRetryConnectPayout}
          inProgress={initiatePayoutInProgess}
          disabled={initiatePayoutInProgess}
        >
          Reintentar transferencia
        </PrimaryButton>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: 'CircularStd' }} >
      {EarningsSectionContent}
      {StuckFundsComponent}
      {clickableContentSection}
      {payoutHistoryContainer}
      
      <GeneratePayoutModal 
        onManageDisableScrolling={onManageDisableScrolling} 
        {...{
          isGeneratePayoutModal, 
          setIsGeneratePayoutModal,
          onHandlePayoutRequest, 
          initiatePayoutInProgess, 
          stripeAccountDataExternalAccount,
          showVerificationNeeded,
          setIsStripeOnboardingModal,
          createStripeConnectAccountLink
        }}
      />
      
      {/* Stripe Onboarding Modal */}
      <Modal
        id="StripeOnboardingModal"
        isOpen={isStripeOnboardingModal}
        onClose={handlePayoutModalClose}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        closeButtonClassName={css.close}
        containerClassName={css.container}
        scrollLayerClassName={css.scrollLayer}
        className={css.isOpen}
        isClosedClassName={css.isClosed}
      >
        <div className={css.modalPayoutDetailsWrapper}>
          <h1 className={css.modalTitle}>
            Datos bancarios
          </h1>
          {!currentUserLoaded ? (
            <FormattedMessage id="StripePayoutPage.loadingData" />
          ) : returnedAbnormallyFromStripe && !stripeAccountLinkError ? (
            <p className={css.modalMessage}>
              <RedirectToStripe redirectFn={createStripeConnectAccountLink} />
            </p>
          ) : (
            <>
              <p className={classNames(css.modalMessage, css.hideOnMobile)}>
                Ingresa tus datos bancarios para recibir pagos.
              </p>
              <p className={classNames(css.modalMessage, css.hideOnMobile)}>
                <FormattedMessage id="EditListingWizard.payoutModalInfo" />
              </p>
              <StripeConnectAccountForm
                disabled={formDisabled}
                inProgress={payoutDetailsSaveInProgress}
                ready={payoutDetailsSaved}
                currentUser={ensuredCurrentUser}
                stripeBankAccountLastDigits={getBankAccountLast4Digits(stripeAccountData)}
                savedCountry={savedCountry}
                submitButtonText={
                  // If already connected and no verification needed, show "Guardar cambios" instead
                  stripeAccountExists && !showVerificationNeeded && savedCountry
                    ? "Guardar cambios"
                    : intl?.formatMessage?.({
                        id: 'StripePayoutPage.submitButtonText',
                      }) || "Guardar detalles"
                }
                stripeAccountError={stripeAccountError}
                stripeAccountFetched={stripeAccountFetched}
                stripeAccountLinkError={stripeAccountLinkError}
                onChange={onPayoutDetailsFormChange}
                onSubmit={handleOnPayoutDetailsFormSubmit}
                onGetStripeConnectAccountLink={createStripeConnectAccountLink}
                stripeConnected={stripeAccountExists}
                onSaveClientClabeNumber={handleOnSaveClientClabeNumber}
                hideHelperText={true}
              >
                {/* Display appropriate Stripe verification UI based on account status */}
                {stripeAccountExists && !returnedAbnormallyFromStripe && showVerificationNeeded ? (
                  <div>
                    <StripeConnectAccountStatusBox
                      type="verificationNeeded"
                      inProgress={getAccountLinkInProgress}
                      onGetStripeConnectAccountLink={createStripeConnectAccountLink(
                        'custom_account_verification'
                      )}
                    />
                  </div>
                ) : stripeAccountExists && savedCountry && !returnedAbnormallyFromStripe ? (
                  <div>
                    <StripeConnectAccountStatusBox
                      type="verificationSuccess"
                      inProgress={getAccountLinkInProgress}
                      disabled={payoutDetailsSaveInProgress}
                      onGetStripeConnectAccountLink={createStripeConnectAccountLink(
                        'custom_account_update'
                      )}
                    />
                  </div>
                ) : null}
              </StripeConnectAccountForm>
            </>
          )}
        </div>
      </Modal>
      
      <InformationModal
        id={'CreditInformation'}
        isOpen={openCreditInformation}
        onClose={toggleCreditInformation}
        text={<FormattedMessage id="EarningsSection.creditInformation" />}
        onManageDisableScrolling={onManageDisableScrolling}
      />

      <InformationModal
        id={'payoutRedimible'}
        isOpen={openPayoutRedimibles}
        onClose={toggleOpenPayoutRedimibles}
        text={<FormattedMessage id="EarningsSection.payoutRedimibles" />}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </div>
  );
}

EarningsSection.propTypes = {
  // from parent component
  userEarnings: PropTypes.number.isRequired,
  userCredits: PropTypes.number.isRequired,
  pendingCredits: PropTypes.number.isRequired,
  userCounter: PropTypes.number,
  userStripeAccountMaybe: PropTypes.string,
  stripeAccountDataExternalAccount: PropTypes.object,
  onHandlePayoutRequest: PropTypes.func.isRequired,
  initiatePayoutInProgess: PropTypes.bool,
  initiatePayoutError: PropTypes.object,
  depositHistoryData: PropTypes.array.isRequired,
  totalPages: PropTypes.number.isRequired,
  paginationInfo: PropTypes.object,
  onLoadNewPage: PropTypes.func.isRequired,
  updateTransfersArray: PropTypes.func,
  onPayoutDetailsFormChange: PropTypes.func.isRequired,
  onPayoutDetailsSubmit: PropTypes.func.isRequired,
  onGetStripeConnectAccountLink: PropTypes.func.isRequired,
  getAccountLinkInProgress: PropTypes.bool.isRequired,
  stripeAccount: PropTypes.object,
  stripeAccountFetched: PropTypes.bool,
  stripeAccountLinkError: PropTypes.object,
  stripeAccountError: PropTypes.object,
  payoutDetailsSaveInProgress: PropTypes.bool.isRequired,
  payoutDetailsSaved: PropTypes.bool.isRequired,
  onSaveClientClabeNumber: PropTypes.func.isRequired,
  onLoadNonCriticalData: PropTypes.func.isRequired,

  // from injectIntl
  intl: PropTypes.object.isRequired,
};

// Default props to handle null values
EarningsSection.defaultProps = {
  userCounter: 0,
};

export default EarningsSection;